import { useEffect, useState } from "react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUBJECTS = [
  "Investment Inquiry",
  "Franchise Opportunity",
  "Education Platform",
  "ParachPay Services",
  "Other",
];

const FRANCHISE_TYPES = ["Solar Project Developer", "Education Hub", "ParachPay Agent"];

export default function Contact({ prefill }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    franchise_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({
        ...f,
        subject: prefill.subject || f.subject,
        franchise_type: prefill.franchise_type || f.franchise_type,
      }));
    }
  }, [prefill]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Thank you — your message has been received. We'll be in touch shortly.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        subject: "",
        franchise_type: "",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email aigltd61@gmail.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5">Get in touch</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] text-[hsl(var(--espresso))]">
              Let's talk about your <span className="italic">next move</span>.
            </h2>
            <p className="mt-6 text-[hsl(var(--fg-muted))] leading-relaxed">
              Questions about our investment opportunities, franchise partnerships, or services?
              Our team is ready to help — investment enquiries, franchise applications, and
              partnership requests all reach us through the form on the right.
            </p>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-white border border-[hsl(var(--line))] rounded-3xl p-8 lg:p-10 shadow-[0_30px_80px_-50px_rgba(28,25,23,0.4)]"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--fg-muted))] mb-2">
                    Full name *
                  </label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={update("full_name")}
                    className="w-full px-4 py-3 rounded-xl bg-[hsl(var(--bg-warm))] border border-[hsl(var(--line))] focus:border-[hsl(var(--terracotta))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--terracotta))]/20 transition"
                    placeholder="Jane Mwangi"
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--fg-muted))] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="w-full px-4 py-3 rounded-xl bg-[hsl(var(--bg-warm))] border border-[hsl(var(--line))] focus:border-[hsl(var(--terracotta))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--terracotta))]/20 transition"
                    placeholder="you@example.com"
                    required
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--fg-muted))] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="w-full px-4 py-3 rounded-xl bg-[hsl(var(--bg-warm))] border border-[hsl(var(--line))] focus:border-[hsl(var(--terracotta))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--terracotta))]/20 transition"
                    placeholder="+254 …"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--fg-muted))] mb-2">
                    Subject *
                  </label>
                  <Select
                    value={form.subject}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        subject: v,
                        franchise_type: v === "Franchise Opportunity" ? f.franchise_type : "",
                      }))
                    }
                  >
                    <SelectTrigger
                      className="w-full px-4 py-3 h-auto rounded-xl bg-[hsl(var(--bg-warm))] border border-[hsl(var(--line))]"
                      data-testid="contact-subject-trigger"
                    >
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`subject-option-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {form.subject === "Franchise Opportunity" && (
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--fg-muted))] mb-2">
                      Franchise model
                    </label>
                    <Select
                      value={form.franchise_type}
                      onValueChange={(v) => setForm((f) => ({ ...f, franchise_type: v }))}
                    >
                      <SelectTrigger
                        className="w-full px-4 py-3 h-auto rounded-xl bg-[hsl(var(--bg-warm))] border border-[hsl(var(--line))]"
                        data-testid="contact-franchise-trigger"
                      >
                        <SelectValue placeholder="Choose a franchise model" />
                      </SelectTrigger>
                      <SelectContent>
                        {FRANCHISE_TYPES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`franchise-option-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--fg-muted))] mb-2">
                    Message *
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-[hsl(var(--bg-warm))] border border-[hsl(var(--line))] focus:border-[hsl(var(--terracotta))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--terracotta))]/20 transition resize-none"
                    placeholder="Tell us a little about what you're looking for…"
                    required
                    data-testid="contact-message"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-pill btn-primary mt-6 w-full sm:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {submitting ? (
                  <>
                    Sending…
                    <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                  </>
                ) : (
                  <>
                    Send message
                    <i className="fa-solid fa-paper-plane text-xs"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
